import { type AxiosError, isAxiosError } from 'axios';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../button';
import { Result } from '../result';
import { ErrorHandler } from './error-handler';

type P<T = unknown> = {
  actions?: boolean;
  children: ReactNode;
  error?: AxiosError<T> | Error | null;
  showError?: boolean;
};

const AxiosErrorHandler = <T = unknown,>({ actions = true, children, error, showError = true }: P<T>) => {
  const navigate = useNavigate();

  return (
    <ErrorHandler<T>
      error={error}
      fallbackRender={(thrownError) => {
        if (!showError) {
          return null;
        }

        if (isAxiosError(thrownError)) {
          if (!thrownError.response) {
            return <Result subTitle="Please check your internet connection." />;
          }

          if (thrownError.response.status === 403) {
            return (
              <Result
                extra={
                  actions ? (
                    <Button
                      onClick={async () => {
                        await navigate('/');
                      }}
                      type="primary"
                    >
                      Home
                    </Button>
                  ) : null
                }
                status="403"
                subTitle="Sorry, you are not authorized to access this resource."
              />
            );
          }

          if (thrownError.response.status === 404) {
            return (
              <Result
                extra={
                  actions ? (
                    <Button
                      onClick={async () => {
                        await navigate('/');
                      }}
                      type="primary"
                    >
                      Home
                    </Button>
                  ) : null
                }
                status="404"
                subTitle="Sorry, resource does not exist."
              />
            );
          }
        }

        return (
          <Result
            extra={
              actions ? (
                <Button
                  onClick={async () => {
                    await navigate(0);
                  }}
                  type="primary"
                >
                  Retry
                </Button>
              ) : null
            }
            status="500"
            subTitle="Sorry, something went wrong."
          />
        );
      }}
    >
      {children}
    </ErrorHandler>
  );
};

export default AxiosErrorHandler;
